// Lightweight daily counter for billable Google Maps Platform calls made by
// this app. Everything lives in localStorage — no backend, no metrics service,
// just enough visibility that the user can see when something is going
// sideways and the client can refuse to keep spending.
//
// Pricing (Oct 2026, Google Maps Platform standard tier):
//   - Maps JS load:              ~$7  / 1000  (28k free/month)
//   - Place Autocomplete session: ~$17 / 1000  (per picked session, w/ fields)
//   - Place Details (basic):      ~$17 / 1000
//   - Place Photos:               ~$7  / 1000
//
// Daily photo-refresh cap is the main lever — the background effect could
// otherwise loop through 200+ places/day. See PRICE_PER_1000 below for the
// cost table used to render a $ estimate in the UI.

const STORAGE_KEY = 'foodlist_api_usage_v1';

// Hard daily ceiling on Place Details calls for photo refresh. At $17/1000
// this caps the background spend at ~$0.17/day, ~$5/month. The expiry window
// is intentionally generous (7 days) so photos stay long enough that the cap
// almost never binds in normal use.
export const DAILY_PHOTO_REFRESH_LIMIT = 10;
export const PHOTO_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export const PRICE_PER_1000 = {
  mapLoad: 7,
  autocompleteSession: 17,
  placeDetails: 17,
  photoRefresh: 17, // same SKU as placeDetails
};

export const KIND_LABELS = {
  mapLoad: 'Map loads',
  autocompleteSession: 'Place picks',
  placeDetails: 'Place details',
  photoRefresh: 'Photo refreshes',
};

function todayKey() {
  // YYYY-MM-DD in the browser's local timezone — matches what the user "feels"
  // as "today" better than UTC does.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function load() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch (_) { return {}; }
}

function save(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
}

function trimToLast30Days(state) {
  const keys = Object.keys(state).sort();
  if (keys.length <= 30) return state;
  const keep = keys.slice(-30);
  const trimmed = {};
  for (const k of keep) trimmed[k] = state[k];
  return trimmed;
}

export function recordCall(kind) {
  const state = load();
  const day = todayKey();
  if (!state[day]) state[day] = {};
  state[day][kind] = (state[day][kind] || 0) + 1;
  save(trimToLast30Days(state));
}

export function getUsage() {
  const state = load();
  const day = todayKey();
  const today = state[day] || {};
  // 30-day totals
  const monthTotals = {};
  for (const d of Object.keys(state)) {
    for (const k of Object.keys(state[d])) {
      monthTotals[k] = (monthTotals[k] || 0) + state[d][k];
    }
  }
  // Estimated $ spend over the recorded window
  let estimateUsd = 0;
  for (const k of Object.keys(monthTotals)) {
    estimateUsd += (monthTotals[k] / 1000) * (PRICE_PER_1000[k] || 0);
  }
  return {
    today,
    monthTotals,
    estimateUsd,
    days: Object.keys(state).length,
  };
}

export function canRefreshPhoto() {
  const today = getUsage().today;
  return (today.photoRefresh || 0) < DAILY_PHOTO_REFRESH_LIMIT;
}

export function resetUsage() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}
